import sortedUniq from 'lodash/sortedUniq';
import {flatten} from 'flat';

import {OFCMinimumStandards} from '@embertracking/common/data/standards/ofc.mjs';

// The reference text (NFPA and WorkSafeBC) and the lookup into it live in
// @embertracking/ui so the hub's Training Matrix dialog can show the same JPRs
// (#2294). Re-exported here so existing imports keep working.
import {NFPA, WorksafeBC, getStandardForKey} from '@embertracking/ui/data/standards';

export {NFPA, WorksafeBC, getStandardForKey};

export const NFPAStandardsMap = (() => {
    const result = [];

    // Recursive function to traverse through the data structure
    const traverse = (obj, standard, edition, path = '') => {
        // Ensure the object exists and is valid for traversal
        if (!obj || typeof obj !== 'object') return;

        Object.keys(obj).forEach(key => {
            // Skip the 'other' property
            if (key === 'other') return;

            // Build the path (e.g., '1001:2019:4.1.1')
            const newPath = path ? `${path}.${key}` : key;
            
            // Check if the current section has a title or text (not all may have both)
            if (obj[key].title || obj[key].text) {
                const fullKey = `${standard}:${edition}:${newPath}`;
                result.push({
                    value: fullKey,
                    label: `NFPA ${standard} (${edition}): ${newPath}`
                });
            }
            
            // Recurse deeper into the structure
            traverse(obj[key], standard, edition, newPath);
        });
    };

    // Traverse the top-level NFPA object
    Object.keys(NFPA).forEach(standard => {
        const editions = NFPA[standard]?.editions;
        if (editions && typeof editions === 'object') {
            Object.keys(editions).forEach(edition => {
                traverse(editions[edition], standard, edition);
            });
        }
    });

    return result;
})();

export const WorksafeBCMap = (() => {
    const result = [];

    for (const part in WorksafeBC) {
        if (Object.hasOwn(WorksafeBC, part)) {
            for (const section in WorksafeBC[part]) {
                if (Object.hasOwn(WorksafeBC[part], section)) {
                    const sectionData = WorksafeBC[part][section];
                    result.push({
                        value: `WorksafeBC:${part}.${section}`,
                        label: `WorkSafeBC: ${part}.${section} ${sectionData.title}`
                    });
                }
            }
        }
    }

    return result;
})();

// The OFC catalog lives in common so the api can use it too (#2204).
export {
    Competencies,
    OFCMinimumStandards,
    CompetencyCategories,
    TechnicalCompetencies,
    getAllCompetencies,
    getCompetenciesByLevel,
    getCompetencyLabel,
    getCompetencyData
} from '@embertracking/common/data/standards/ofc.mjs';

export const Standards = [...NFPAStandardsMap, ...WorksafeBCMap].map(({value, ...rest}) => ({
    ...getStandardForKey(value),
    ...rest,
    value
}));

const allMinimumStandards = sortedUniq(Object.values(flatten(OFCMinimumStandards)).filter(value => typeof value === 'string'));
const missingStandards = allMinimumStandards.map(key => {
    return {
        key,
        standard: getStandardForKey(key)
    };
}).filter(({standard}) => !standard).map(({key}) => key);

if (missingStandards.length) {
    console.warn('Missing Standards:', missingStandards);
}